import React from 'react';
import './AeroFeatureGate.css';

// Same tier ladder as the figmafolio FeatureGate; 'free' is accepted as an
// alias because AeroLayout exposes planKey 'free' for 'regular' users.
const TIER_RANK = { regular: 0, free: 0, basic: 1, solo: 2, pro: 3 };

// Two-tone, FILLED illustrations for the upsell banners. Dark Aero blue
// (#3D78B0) paired with lighter blues (#9DBED9 / #cfe0ef), ~40px, shown with a
// soft drop shadow and NO chip/box behind them (see .aero-feature-gate-icon in
// the stylesheet). Each banner picks one by the `icon` prop (a glyph name):
//   window  — Site address      lock    — Password
//   sparkle — Favicon           chart   — Analytics
//   badge   — "Made with Aero"  globe   — Custom domain
const ICONS = {
  window: (
    <svg viewBox="0 0 40 40">
      <rect x="6" y="9" width="28" height="22" rx="5" className="g3" />
      <path d="M6 14a5 5 0 0 1 5-5h18a5 5 0 0 1 5 5v1H6z" className="g1" />
      <circle cx="10.5" cy="12" r="1.2" className="g4" />
      <circle cx="14.2" cy="12" r="1.2" className="g4" />
      <rect x="11" y="20" width="18" height="3.2" rx="1.6" className="g2" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 40 40">
      <path d="M14 19v-4a6 6 0 0 1 12 0v4" className="s2" strokeWidth="4" strokeLinecap="round" fill="none" />
      <rect x="10" y="18" width="20" height="15" rx="4.5" className="g1" />
      <circle cx="20" cy="24.5" r="2.3" className="g3" />
      <rect x="18.8" y="24.5" width="2.4" height="5" rx="1.2" className="g3" />
    </svg>
  ),
  sparkle: (
    <svg viewBox="0 0 40 40">
      <path d="M19 8l2.7 7.6L29 18l-7.3 2.4L19 28l-2.7-7.6L9 18l7.3-2.4z" className="g1" />
      <path d="M30 9l1.1 3 3 1.1-3 1.1-1.1 3-1.1-3-3-1.1 3-1.1z" className="g2" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 40 40">
      <rect x="9" y="21" width="6" height="11" rx="2.2" className="g2" />
      <rect x="17" y="13" width="6" height="19" rx="2.2" className="g1" />
      <rect x="25" y="24" width="6" height="8" rx="2.2" className="g2" />
    </svg>
  ),
  badge: (
    <svg viewBox="0 0 40 40">
      <path d="M15 22l-3 11 8-4 8 4-3-11z" className="g2" />
      <circle cx="20" cy="16" r="10" className="g1" />
      <circle cx="20" cy="16" r="4.2" className="g3" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 40 40">
      <defs>
        <clipPath id="aeroGateGlobe">
          <circle cx="20" cy="20" r="13" />
        </clipPath>
      </defs>
      <circle cx="20" cy="20" r="13" className="g1" />
      <g clipPath="url(#aeroGateGlobe)">
        <ellipse cx="20" cy="20" rx="5.5" ry="13" className="g2" />
        <rect x="6" y="11.6" width="28" height="3.2" className="g2" />
        <rect x="6" y="25.2" width="28" height="3.2" className="g2" />
      </g>
      <circle cx="20" cy="20" r="2.6" className="g3" />
    </svg>
  ),
};

/**
 * The accent-tinted upsell banner: [icon] [title + desc] [CTA]. Shared by the
 * gated sections (rendered automatically by AeroFeatureGate) and by info-only
 * placements that show the same banner without a gated preview (e.g. the
 * Settings "Site label" section). No border — only the soft tinted fill.
 *
 * @param {string} icon - Glyph name from ICONS ('window' | 'lock' | …); optional
 * @param {boolean} inline - Drop the horizontal margins when the banner already
 *   sits inside a padded card
 * @param {string} position - 'top' to add the above-preview margin variant
 */
export function AeroUpsellBanner({ icon, title, message, onUpgradeClick, position, inline }) {
  const glyph = icon ? ICONS[icon] : null;
  return (
    <div
      className={`aero-feature-gate-upsell${position === 'top' ? ' aero-feature-gate-upsell-top' : ''}${
        inline ? ' aero-feature-gate-upsell-inline' : ''
      }`}
    >
      {glyph && <div className="aero-feature-gate-icon">{glyph}</div>}
      <div className="aero-feature-gate-copy">
        {title && <div className="aero-feature-gate-title">{title}</div>}
        {message && <div className="aero-feature-gate-message">{message}</div>}
      </div>
      <button
        type="button"
        className="aero-btn aero-btn-primary aero-feature-gate-cta"
        onClick={onUpgradeClick}
      >
        Upgrade now
      </button>
    </div>
  );
}

/**
 * AeroFeatureGate gates an Aero section by subscription tier, mirroring the
 * figmafolio FeatureGate pattern in the glass design system. With access it
 * renders children untouched. Without access it shows the section disabled at
 * reduced opacity (pointer-events none, no blur) plus an accent-tinted upsell
 * banner with a two-tone graphic, a bold title and a benefit line (no tier
 * named on purpose — the CTA lets users compare plans and decide) and an
 * "Upgrade now" button that opens the Aero upgrade modal IN PLACE via
 * onUpgradeClick, so the user never leaves the current page.
 *
 * @param {string} requiredTier - Minimum tier needed ('basic' | 'solo' | 'pro')
 * @param {string} currentTier - The user's subscriptionType from the AeroLayout outlet context
 * @param {string} title - Bold benefit headline
 * @param {string} message - One benefit line under the title
 * @param {string} icon - Glyph name for the banner graphic (see ICONS)
 * @param {function} onUpgradeClick - Opens the upgrade modal inline (no navigation)
 * @param {string} upsellPosition - 'bottom' (default) or 'top' (above the preview)
 * @param {ReactNode} children - The gated section
 */
function AeroFeatureGate({
  requiredTier,
  currentTier = 'regular',
  title,
  message,
  icon,
  onUpgradeClick,
  upsellPosition = 'bottom',
  children,
}) {
  const hasAccess = (TIER_RANK[currentTier] || 0) >= (TIER_RANK[requiredTier] || 0);

  if (hasAccess) {
    return children;
  }

  const upsell = (
    <AeroUpsellBanner
      icon={icon}
      title={title}
      message={message}
      onUpgradeClick={onUpgradeClick}
      position={upsellPosition === 'top' ? 'top' : undefined}
    />
  );

  return (
    <div className="aero-feature-gate">
      {upsellPosition === 'top' && upsell}
      <div className="aero-feature-gate-preview" aria-disabled="true">
        {children}
      </div>
      {upsellPosition !== 'top' && upsell}
    </div>
  );
}

export default AeroFeatureGate;
